import ApiService from './api';

const BotService = {
  get: async function () {
    try {
      const response = await ApiService.get('/bot');
      return response.data;
    } catch (error) {
      return [];
    }
  },
  getBot: async function (id) {
    try {
      const response = await ApiService.get(`/schaltzentrale/bot/${id}`);
      console.log('THE BOT', response.data);
      return response.data;
    } catch (error) {
      return [];
    }
  },
  /**
   * Updates the bot's name.
   * @param {string} botId - The bot's unique ID.
   * @param {string} name - The new name for the bot.
   * @returns {Promise<Object|false>} - Updated bot data or false on error.
   */
  async updateBotName(botId, name) {
    try {
      const data = await ApiService.put(`/schaltzentrale/bot/${botId}`, { name });
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  },
  hasActiveChannel: function (bot, type) {
    if (type === 'widget') return true;

    if (!bot || !Array.isArray(bot.channels)) return false;
    if (bot.channels.some((c) => c.channel === type && c.active)) {
      return true;
    }

    return false;
  },
  linkToChannel: function (bot, type) {
    if (!bot || !Array.isArray(bot.channels)) return '';
    const channel = bot.channels.find((c) => c.channel === type);

    if (type === 'facebook') {
      return 'https://m.me/' + channel.channelId;
    }

    if (type === 'widget') {
      return (
        'https://preview.moin.ai/?bot=' + (bot.stagingBot || bot.uniqueBotId)
      );
    }

    return '';
  },
  async saveSettings(botid, settings, settingType = '') {
    try {
      const response = await ApiService.post(
        `/schaltzentrale/bot/settings/${botid}/${settingType}`,
        settings
      );
      return response?.data;
    } catch (error) {
      throw new Error(error);
    }
  },
  async newBot({ name, language, fallbackFaqIntent }) {
    try {
      const response = await ApiService.post(`/bot/`, { name, language, fallbackFaqIntent: { active: fallbackFaqIntent } });
      return response;
    } catch (error) {
      return false;
    }
  },
  async getAccounts(botId) {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/bot/accounts/${botId}/`
      );
      return response.data;
    } catch (error) {
      return false;
    }
  },
  async getBotInvites(botId) {
    try {
      const response = await ApiService.get(`/bot/botinvites/${botId}/`);
      return response.data;
    } catch (error) {
      return false;
    }
  },
  async addChannel(channel) {
    try {
      const response = await ApiService.post(`/bot/channel/`, channel);
      return response.data;
    } catch (error) {
      return false;
    }
  },

  /**
   * Updates a bot channel only with the allowed fields
   * @param {Object} params
   * @param {string} params.uniqueBotId - Bot ID
   * @param {Object} params.channel - Channel update data (must include channelId)
   * @param {string} params.channel.channelId - ID of the channel to update (required)
   * @param {boolean} [params.channel.active] - Whether the channel is active
   * @param {string} [params.channel.channel] - Type of the channel (e.g., 'facebook', 'widget')
   * @param {Object} [params.channel.payload] - Additional payload data for the channel
   * @param {string} [params.channel.displayName] - Display name for the channel
   * @returns {Promise<{ success: boolean, data?: any, error?: string }>}
   */
  async updateChannel({ botId, channel = {} } = {}) {
    const { channelId, active, channel: channelType, payload, displayName } = channel;
    if (!channelId || !botId) return { success: false, error: 'channelId and botId are required' };

    const body = { uniqueBotId: botId, channelId };
    if (typeof active !== 'undefined') body.active = active;
    if (typeof channelType !== 'undefined') body.channel = channelType;
    if (typeof payload !== 'undefined') body.payload = payload;
    if (typeof displayName !== 'undefined') body.displayName = displayName;

    try {
      const response = await ApiService.post(`/bot/channel/update`, body);
      // Because of 400 interceptor for moin-api
      if (!response || !response.data || typeof response.data !== 'object') {
        const error = response.data || 'Something went wrong while updating the channel';
        return { success: false, error };
      }

      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error?.response?.data || 'Failed to update channel' };
    }
  },
  // @data: {active: boolean, features: string[]}
  async updateUser(email, data) {
    try {
      const response = await ApiService.post(
        `/schaltzentrale/bot/accounts/${email}`,
        data
      );
      return response.data;
    } catch (error) {
      return false;
    }
  },

  async deleteUser(email, botId) {
    try {
      const response = await ApiService.delete(
        `/schaltzentrale/bot/accounts/${email}`,
        { uniqueBotId: botId }
      );
      return response.data;
    } catch (error) {
      return false;
    }
  },

  async getNlPFeatures(botId) {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/nlp/${botId}/features`
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  async activateKeywords({ botIdLive, botIdStaging }) {
    try {
      const resLive = await ApiService.post(
        `/schaltzentrale/nlp/${botIdLive}/features/keywordmatch`
      );
      const resStaging = await ApiService.post(
        `/schaltzentrale/nlp/${botIdStaging}/features/keywordmatch`
      );

      if (resLive?.status !== 200 || resStaging?.status !== 200) return false;
      return true;
    } catch (error) {
      return {};
    }
  },
  async deactivateKeywords({ botIdLive, botIdStaging }) {
    try {
      const resLive = await ApiService.delete(
        `/schaltzentrale/nlp/${botIdLive}/features/keywordmatch`
      );
      const resStaging = await ApiService.delete(
        `/schaltzentrale/nlp/${botIdStaging}/features/keywordmatch`
      );
      if (resLive?.status !== 200 || resStaging?.status !== 200) return false;
      return true;
    } catch (error) {
      return false;
    }
  },
  async set2FAActivation({ botId, activate, forSSO }) {
    try {
      const response = await ApiService.post(`/bot/2FA/${botId}`, {
        activate,
        forSSO: !!forSSO,
      });

      return response?.data;
    } catch (error) {
      return false;
    }
  },
  async setSSOSettings({botId, provider, sso2FA}) {
    try {
      const response = await ApiService.post(`/schaltzentrale/bot/settings/${botId}/sso`, {
        provider,
        sso2FA,
      });

      return response?.data;
    } catch (error) {
      return false;
    }
  },
  // @data example {
  //     "demoStepId": "640f2763baff1cdfdbccd146",
  //     "createAnswer": false,
  //     "selectTopics": [9,10,11,12],
  //     "advancedSamples": true
  // }
  async updateDemoBotIntents(botId, data) {
    try {
      const response = await ApiService.post(
        `/schaltzentrale/intentbootstrap/fromdemostep/${botId}`,
        data
      );
      return response?.data;
    } catch (error) {
      return false;
    }
  },
  async setHighPrivacyActivation({ botId, activate }) {
    try {
      const response = await ApiService.post(
        `/schaltzentrale/nlp/anonymization/${botId}/${
          activate ? 'enable' : 'disable'
        }`
      );

      return response?.data;
    } catch (error) {
      return false;
    }
  },
  async searchBot(botId, query) {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/bot/${botId}/search/${query}`
      );
      return response?.data;
    } catch (error) {
      return false;
    }
  },
  async getContentByAction(botId, actionName) {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/bot/${botId}/actionname/${actionName}`
      );
      if (response?.data?.error) {
        console.error("Loading content error: ", response?.data?.error);
        return false;
      }
      return response?.data;
    } catch (error) {
      return false;
    }
  },
  async updateEditedAction(uniqueBotId, action) {
    try {
      const response = await ApiService.post(`/bot/actions/edit`, {action, uniqueBotId});
      return response?.status === 200;
    } catch (error) {
      return false;
    }
  },

  /** @typedef {{ type: 'regexp', displayName: string, expression: string, parameter: string }} RegexpEntity */
  /**
   * 
   * @param {string} botId 
   * @returns {RegexpEntity[]}
   */
  async getAllRegEpx(botId) {
    try {
      const response = await ApiService.get(`/schaltzentrale/regexp/${botId}/all/`);
      return response.data;
    } catch (error) {
      return false;
    }
  },
  /**
   * 
   * @param {string} botId 
   * @param {[any]} data 
   * @returns {Promise<
   *  { success: true, data: any[] } |
   *  { success: false, error: { code, status, message } }
   * >}
   */
  async setRegExp(botId, data) {
    try {
      const response = await ApiService.put(`/schaltzentrale/regexp/${botId}`, data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },
  
  /**
   * Creates a new API key for a bot
   * 
   * @param {string} uniqueBotId - ID of the bot
   * @returns {Promise<Object|{error: string}>} - Created API key data or error object
   */
  async createApiKey(uniqueBotId) {
    try {
      const response = await ApiService.post('/bot/apikeys', { uniqueBotId });
      return response.data.key;
    } catch (error) {
      console.error('[createApiKey] Error:', error?.message || error);
      return { 
        error: error?.response?.data?.message || 'Failed to create API key'
      };
    }
  },
  
  /**
   * Updates an API key status (activate/deactivate)
   * 
   * @param {Object} updateData - Data needed to update an API key
   * @param {string} updateData.botId - ID of the bot
   * @param {string} updateData.apiKey - The API key to update
   * @param {boolean} updateData.active - New status for the API key
   * @returns {Promise<Object|{error: string}>} - Success confirmation or error object
   */
  async updateApiKeyStatus(updateData) {
    try {
      const response = await ApiService.put('/bot/apikeys', updateData);
      return response.data;
    } catch (error) {
      console.error('[updateApiKeyStatus] Error:', error?.message || error);
      return { 
        error: error?.response?.data?.message || 'Failed to update API key status'
      };
    }
  },
  
  /**
   * Soft-deletes an API key
   * 
   * @param {string} uniqueBotId - ID of the bot
   * @param {string} key - The API key to delete
   * @returns {Promise<Object|{error: string}>} - Success confirmation or error object
   */
  async deleteApiKey(uniqueBotId, key) {
    try {
      const response = await ApiService.delete('/bot/apikeys', { uniqueBotId, key });
      return { success: response.status === 200 };
    } catch (error) {
      console.error('[deleteApiKey] Error:', error?.message || error);
      return { 
        error: error?.response?.data?.message || 'Failed to delete API key'
      };
    }
  },
  
  /**
   * Gets the latest active API key for a bot
   * 
   * @param {string} botId - ID of the bot
   * @returns {Promise<Object|{error: string, status: number}>} - Latest active API key or error object
   */
  async getLatestApiKey(botId) {
    try {
      const response = await ApiService.get(`/bot/apikeys/${botId}/latest`);
      return response.data.key;
    } catch (error) {
      console.error('[getLatestApiKey] Error:', error?.message || error);
      // Return 404 status if no keys exist
      if (error?.response?.status === 404) {
        return { 
          error: 'No active API keys found',
          status: 404
        };
      }
      return { 
        error: error?.response?.data?.message || 'Failed to retrieve latest API key',
        status: error?.response?.status || 400
      };
    }
  },
  
  /**
   * Creates a new API key while deactivating the most recently created active key
   * 
   * @param {string} uniqueBotId - ID of the bot
   * @returns {Promise<Object|{error: string}>} - Newly created API key or error object
   */
  async regenerateApiKey(uniqueBotId) {
    try {
      // Call the backend endpoint directly which handles the regeneration process
      const response = await ApiService.post(`/bot/apikeys/regenerate`, { uniqueBotId });
      return response.data.key;
    } catch (error) {
      console.error('[regenerateApiKey] Error:', error?.message || error);
      return { 
        error: error?.response?.data?.message || 'Failed to regenerate API key',
        status: error?.response?.status || 400
      };
    }
  },
  
  async getNlp({ botId, sensitive }) {
    const LOG_PREFIX = `[getNlp]`;
    const query = sensitive === true ? '?sensitive=true' : '';
    try {
      const response = await ApiService.get(`/schaltzentrale/nlp/${botId}/management${query}`);
      return response.data || [];
    } catch (error) {
      console.error(`${LOG_PREFIX} Error when fetching NLP(s). Error: `, error?.message || error);
      return [];
    }
  },
  async saveNlp({ botId, nlp }) {
    const LOG_PREFIX = `[getNlp]`;

    const data = { nlp };
    // data.autoConfiguration = false
    // In case you want to replace it entirely (not including RegExp). Without missed fields generation

    try {
      const response = await ApiService.put(`/schaltzentrale/nlp/${botId}/management`, data);
      return {
        success: response.data?.success === true,
        data: response.data?.data
      };
    } catch (error) {
      const errorData = error?.response?.data?.error  || error?.response?.data || error;
      return { success: false, error: errorData };
    }
  },
};

export default BotService;

export { BotService };
